import { Component } from 'react';
import AllBranchesModalBranch from './components/AllBranchesModalBranch.tsx/AllBranchesModalBranch';
import { defaultPagination, PER_PAGE } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { IBranch } from 'src/types/branches';
import { IPagination, MODALS_CRUMB, TBooleanInNumber, TTranslate } from 'src/types/main';
import { emptyArray, getUnavailable } from 'src/utils/helpers';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalFooter from 'src/view/components/Controls/Modal/ModalFooter/ModalFooter';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import { BranchesModalHeader } from 'src/view/containers/Modals/BranchesModal/containers/BranchesModalHeader/BranchesModalHeader';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import logger from '~/services/logger';

export interface IAllBranchesModalItem {
  id: number;
  name: TTranslate;
  is_busy: boolean;
  is_delete: TBooleanInNumber;
  status: TBooleanInNumber;
}

interface IState {
  selectedBranches: IAllBranchesModalItem[];
  selectedUnavailable: IAllBranchesModalItem[];
  search: string;
  activeCrumb: MODALS_CRUMB;
  pagination: IPagination<IBranch>;
  loading: boolean;
  isAll: boolean;
}

export default class AllBranchesModal extends Component<{}, IState> {
  state = {
    selectedBranches: modalStore.allBranchesModal?.selectedBranches || [],
    selectedUnavailable: modalStore.allBranchesModal?.selectedUnavailable || [],
    search: '',
    activeCrumb: MODALS_CRUMB.ALL,
    pagination: defaultPagination,
    loading: false,
    isAll: modalStore.allBranchesModal?.isAll || false,
  };

  closeModal = () => {
    modalStore.closeAllBranchesModal();
  };

  componentDidMount = () => {
    this.fetchBranches(1);
  };

  fetchBranches = async (page: number = this.state.pagination.current_page + 1) => {
    try {
      if (this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.selectedBranches.length && !this.state.isAll) return;
      this.setState({ loading: true });
      const branches: IPagination<IBranch> = await client.get(`/v4/branches`, {
        params: {
          page,
          name: this.state.search,
          is_delete: 0,
          status: 1,
          branches:
            this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.isAll
              ? this.state.selectedBranches.map((branch) => branch.id)
              : undefined,
          per_page: PER_PAGE.DEFAULT,
        },
      });

      this.setState({ pagination: branches, loading: false });
    } catch (error: any) {
      logger.error({ error, place: 'AllBranchesModal fetchBranches' });
      this.setState({ loading: false });
    }
  };

  clickBranch = (branch?: IAllBranchesModalItem) => {
    if (!branch) return;

    if (modalStore.allBranchesModal?.isMulti) {
      if (this.state.isAll) {
        if (this.state.activeCrumb === MODALS_CRUMB.SELECTED) {
          this.setState({ selectedBranches: [branch], isAll: false }, () => this.clickCrumb(MODALS_CRUMB.ALL));
        } else {
          this.setState({ selectedBranches: [branch], isAll: false });
        }
      } else if (this.state.selectedBranches?.map((item) => item.id).includes(branch.id)) {
        this.setState({ selectedBranches: this.state.selectedBranches.filter((item) => item.id !== branch.id) });
      } else {
        this.setState({ selectedBranches: this.state.selectedBranches ? this.state.selectedBranches.concat(branch) : [branch] });
      }
    } else {
      this.setState({ selectedBranches: [branch] });
    }
  };

  onSearch = (value: string) => {
    this.setState({ search: value }, () => this.fetchBranches(1));
  };

  clickCrumb = (value: MODALS_CRUMB) => {
    this.setState({ activeCrumb: value }, () => this.fetchBranches(1));
  };

  clickSelectAll = () => {
    if (this.state.search) return;
    if (this.state.activeCrumb === MODALS_CRUMB.SELECTED) {
      this.setState({ selectedBranches: [], isAll: true }, () => this.fetchBranches(1));
    } else {
      this.setState({ selectedBranches: [], isAll: true });
    }
  };

  clickDeselectAll = () => {
    this.setState({ selectedBranches: modalStore.allBranchesModal?.isMulti ? this.state.selectedUnavailable : [], isAll: false });
  };

  clickSave = () => {
    modalStore.allBranchesModal?.onSave(this.state.selectedBranches);
  };

  getRenderArr = () => {
    if (modalStore.allBranchesModal?.isMulti) {
      if (this.state.selectedUnavailable.length && this.state.pagination.current_page === this.state.pagination.last_page) {
        return [...this.state.pagination.data, ...this.state.selectedUnavailable];
      } else return this.state.pagination.data;
    } else {
      if (this.state.activeCrumb === MODALS_CRUMB.SELECTED) {
        return this.state.selectedBranches;
      } else return [...this.state.pagination.data, ...this.state.selectedUnavailable];
    }
  };

  renderBranches = () => {
    if (this.state.loading) {
      return emptyArray().map((_, index) => <AllBranchesModalBranch key={'key-' + String(index)} clickBranch={this.clickBranch} />);
    } else if (this.noBranchesForRender) {
      return (
        <div className="d-flex full-height full-width align-items-center">
          <p className="family-bold text-size-26 color-dark mx-auto my-auto">{localeStore.t('branches_not_found')}</p>
        </div>
      );
    } else {
      return this.getRenderArr().map((branch: IAllBranchesModalItem) => (
        <AllBranchesModalBranch
          key={branch.id}
          branch={branch}
          active={this.state.isAll ? true : this.state.selectedBranches.some((item) => item.id === branch.id)}
          noAvailable={getUnavailable(branch)}
          clickBranch={this.clickBranch}
        />
      ));
    }
  };

  get noBranchesForRender(): boolean {
    switch (true) {
      case !this.state.selectedBranches.length && this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.isAll:
      case !this.state.pagination.data.length && !this.state.selectedUnavailable.length:
        return true;
      default:
        return false;
    }
  }

  get headerTitle(): string {
    if (modalStore.allBranchesModal?.headerTitle) {
      return modalStore.allBranchesModal?.headerTitle;
    } else if (modalStore.allBranchesModal?.isMulti) {
      return localeStore.t('select_branches');
    } else return localeStore.t('select_branch');
  }

  render() {
    return (
      <CustomModalComponent isOpen={true} onRequestClose={this.closeModal} className="custom-modal" withCloseIcon>
        <ModalBackground>
          <ModalHeader title={this.headerTitle} />
          <BranchesModalHeader
            searchValue={this.state.search}
            activeCrumb={this.state.activeCrumb}
            selectedItemsLength={this.state.selectedBranches.length - this.state.selectedUnavailable.length}
            isAll={this.state.isAll}
            selectedBranches={this.state.selectedBranches}
            isMulti={Boolean(modalStore.allBranchesModal?.isMulti)}
            onSearch={this.onSearch}
            loading={this.state.loading}
            clickCrumb={this.clickCrumb}
            selectAll={this.clickSelectAll}
            deselectAll={this.clickDeselectAll}
            hideSelectAllButtonWhenSearch
          />

          <ScrollableBlock showScrollbar className="d-flex flex-1 mx-8" scrollClassName="full-height">
            <ul className="custom-modal__container mb-2">{this.renderBranches()}</ul>
          </ScrollableBlock>
          {this.noBranchesForRender ? null : (
            <div className="mt-2">
              <Pagination pagination={this.state.pagination} onChange={this.fetchBranches} />
            </div>
          )}

          <ModalFooter>
            <Button title={localeStore.t('Save')} onClick={this.clickSave} loading={this.state.loading} size="large" />
          </ModalFooter>
          {this.state.loading ? <LoadingOverlay /> : null}
        </ModalBackground>
      </CustomModalComponent>
    );
  }
}
